/**
 * `/lists/:id` — members and share state (design §2.2).
 *
 * The share affordance here is the LIST grant, which is a different object from
 * a contact grant and is deliberately not conflated with one: `shares.list.*`
 * writes a row against the list, and the scope applies to every member it
 * exposes. Design §2.10 also describes a `view` / `contribute` distinction and
 * an "include member contact details" toggle; `listShareSchema` has neither —
 * it carries the same three-rung `shareScope` a contact grant does — so the
 * dialog offers the rungs that exist rather than inventing two more.
 *
 * ## Membership, and the paging that used to be a placard
 *
 * `lists.get` takes `limit`/`cursor` and returns `membersNextCursor`; the screen
 * fetched page one and put "Only the first page of members is fetched" under it.
 * It pages now. `lists.items.add` / `.remove` were likewise already real and now
 * have buttons — with one server constraint respected: `note` is a 422, so the
 * add flow does not offer one.
 *
 * Removal is a REMOVE FROM LIST, not a delete, and the confirm copy says so —
 * `listsRepo.removeItems` touches the membership row and nothing else.
 */

import { Badge, Button, EmptyState, SkeletonRows, toast } from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Share2Icon, UserMinusIcon, UserPlusIcon } from 'lucide-react';
import { useState } from 'react';
import {
  useAddListItemsMutation,
  useListQuery,
  useRemoveListItemsMutation,
} from '../../../api/queries.ts';
import { useOutgoingSharesQuery, useRevokeShareMutation } from '../../../api/sharing.ts';
import { ConfirmDialog } from '../../../components/confirm-dialog.tsx';
import { ContactPicker } from '../../../components/contacts/contact-picker.tsx';
import { ContactTypeBadge } from '../../../components/contacts/contact-type-badge.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { GrantShareDialog } from '../../../components/sharing/grant-share-dialog.tsx';
import { ShareChip } from '../../../components/sharing/share-chip.tsx';
import { scopeLabel } from '../../../components/sharing/share-scope.ts';
import { toClientError } from '../../../lib/errors.ts';
import { fullName } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/lists/$listId')({
  component: ListDetailPage,
  errorComponent: RouteErrorPanel,
});

/**
 * This list's outgoing grants.
 *
 * Same shape as the contact panel's: `sharesOutgoingInputSchema` has no
 * `resourceId`, so the org's list grants are read and filtered here, and the
 * footer says so when the page was full rather than implying the list is
 * complete.
 */
function ListShareState({ listId, listName }: { listId: string; listName: string }) {
  const [granting, setGranting] = useState(false);
  const outgoing = useOutgoingSharesQuery({ resourceType: 'list' });
  const revoke = useRevokeShareMutation();

  const rows = (outgoing.data?.items ?? []).filter(
    (share) => share.resourceId === listId && share.revokedAt === null,
  );
  const mayBePartial = outgoing.data?.nextCursor != null;

  return (
    <section
      aria-labelledby="list-sharing-heading"
      className="rounded-lg border border-border p-4 text-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="list-sharing-heading" className="font-medium">
          Sharing
        </h2>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto max-sm:w-full"
          onClick={() => setGranting(true)}
        >
          <Share2Icon aria-hidden className="size-3.5" />
          Share this list
        </Button>
      </div>

      {outgoing.isLoading ? (
        <SkeletonRows rows={2} className="mt-3" />
      ) : outgoing.isError ? (
        <p className="mt-2 text-muted-foreground">Could not load this list’s grants.</p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-muted-foreground">
          Not shared with anyone{mayBePartial ? ' on the first page of grants' : ''}.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {rows.map((share) => (
            <li key={share.id} className="flex flex-wrap items-center gap-2">
              <span>
                {share.granteeType === 'org'
                  ? (share.granteeOrgName ?? share.granteeOrgId)
                  : (share.granteeUserEmail ?? share.granteeUserId)}
              </span>
              <Badge variant="secondary">{scopeLabel(share.scope)}</Badge>
              <span className="text-xs text-muted-foreground">
                exposes {share.contactCount} contact{share.contactCount === 1 ? '' : 's'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto text-destructive"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate({ shareId: share.id, resourceType: 'list' })}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        A list grant exposes its members at the chosen scope. Adding someone to a shared list shares
        them too — that is what makes list sharing useful, and what makes it worth checking before
        you add.
      </p>

      {granting ? (
        <GrantShareDialog
          open
          onOpenChange={setGranting}
          resourceType="list"
          resourceId={listId}
          resourceLabel={listName}
        />
      ) : null}
    </section>
  );
}

function AddMembersDialog({
  listId,
  listName,
  memberIds,
  open,
  onOpenChange,
}: {
  listId: string;
  listName: string;
  memberIds: readonly string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const add = useAddListItemsMutation();
  const [selected, setSelected] = useState<readonly string[]>([]);

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSelected([]);
        onOpenChange(next);
      }}
      title={`Add people to “${listName}”`}
      description="Pick anyone you own. They keep every other list they are on."
      confirmLabel={selected.length === 0 ? 'Add' : `Add ${selected.length}`}
      destructive={false}
      onConfirm={async () => {
        if (selected.length === 0) {
          throw new Error('Pick at least one contact first.');
        }
        const result = await add.mutateAsync({ listId, contactIds: selected });
        setSelected([]);
        toast.success(
          `Added ${result.changed} to ${listName}`,
          result.skipped > 0
            ? {
                // `addItems` returns one number for two causes and the client
                // cannot tell them apart, so neither is asserted.
                description: `${result.skipped} were skipped — already on the list, or not yours to add.`,
              }
            : undefined,
        );
      }}
    >
      <div className="mt-2">
        <ContactPicker
          selected={selected}
          excludeIds={memberIds}
          emptyLabel="You have no contacts to add yet."
          onToggle={(id) =>
            setSelected((current) =>
              current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
            )
          }
        />
      </div>
    </ConfirmDialog>
  );
}

function ListDetailPage() {
  const { listId } = Route.useParams();
  /**
   * The member page cursor stack.
   *
   * A stack rather than a single cursor because `lists.get` is forward-only: to
   * go BACK a page the previous cursor has to be remembered. Keeping the whole
   * trail also makes "Page 3 of ?" honest — the server never says how many
   * there are.
   */
  const [cursors, setCursors] = useState<readonly (string | null)[]>([null]);
  const cursor = cursors.at(-1) ?? null;
  const list = useListQuery(listId, cursor);

  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const removeItems = useRemoveListItemsMutation();

  if (list.isLoading) {
    return <SkeletonRows rows={6} className="p-4" />;
  }
  if (list.isError || list.data === undefined) {
    return (
      <EmptyState
        kind="error"
        className="m-4"
        title="Could not load this list"
        description={list.error === null ? undefined : toClientError(list.error).message}
      />
    );
  }

  const detail = list.data;
  const memberIds = detail.members.map((member) => member.id);
  const page = cursors.length;

  return (
    <>
      <PageHeader
        title={detail.name}
        description={detail.description ?? `${detail.itemCount} contacts · ${detail.kind} list`}
        actions={
          <>
            <Badge variant="muted">{detail.visibility}</Badge>
            {detail.access.canEdit ? (
              <Button size="sm" onClick={() => setAdding(true)}>
                <UserPlusIcon aria-hidden />
                Add people
              </Button>
            ) : null}
          </>
        }
      />
      <PageBody>
        <ListShareState listId={listId} listName={detail.name} />

        {detail.members.length === 0 ? (
          <EmptyState
            title={page === 1 ? 'This list has no members yet' : 'No more members'}
            description={page === 1 ? 'Use “Add people” to put someone on it.' : undefined}
            {...(page === 1 && detail.access.canEdit
              ? {
                  action: (
                    <Button size="sm" onClick={() => setAdding(true)}>
                      Add people
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {detail.members.map((member) => (
              <li key={member.id} className="flex items-stretch">
                <Link
                  to="/contacts/$contactId"
                  params={{ contactId: member.id }}
                  className="flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-0.5 px-4 py-2.5 text-sm hover:bg-accent/50 sm:flex-row sm:items-center sm:gap-3"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium">
                      {fullName(member.firstName, member.lastName)}
                    </span>
                    <ContactTypeBadge type={member.contactType} />
                    <ShareChip
                      compact
                      source={member.access.source}
                      ownerOrgName={member.access.ownerOrgName}
                      scope={member.access.scope}
                    />
                  </span>
                  <span className="truncate text-xs text-muted-foreground sm:ml-auto">
                    {member.companyName ?? ''}
                  </span>
                </Link>
                {detail.access.canEdit ? (
                  <div className="flex shrink-0 items-center pr-2">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-destructive"
                      aria-label={`Remove ${fullName(member.firstName, member.lastName)} from this list`}
                      onClick={() =>
                        setRemoving({
                          id: member.id,
                          name: fullName(member.firstName, member.lastName),
                        })
                      }
                    >
                      <UserMinusIcon aria-hidden />
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {cursors.length > 1 || detail.membersNextCursor != null ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={cursors.length === 1}
              onClick={() => setCursors((stack) => stack.slice(0, -1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={detail.membersNextCursor == null}
              onClick={() => setCursors((stack) => [...stack, detail.membersNextCursor ?? null])}
            >
              Next
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page} · {detail.itemCount} member{detail.itemCount === 1 ? '' : 's'} in total
            </span>
          </div>
        ) : null}

        <AddMembersDialog
          listId={listId}
          listName={detail.name}
          memberIds={memberIds}
          open={adding}
          onOpenChange={setAdding}
        />

        {removing === null ? null : (
          <ConfirmDialog
            open
            onOpenChange={(open) => {
              if (!open) setRemoving(null);
            }}
            title={`Remove ${removing.name} from “${detail.name}”?`}
            description="The contact and everything on their timeline stay exactly as they are. Only their membership of this list goes."
            confirmLabel="Remove from list"
            onConfirm={() => removeItems.mutateAsync({ listId, contactIds: [removing.id] })}
          />
        )}
      </PageBody>
    </>
  );
}
